import { useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import PartAssemblySingleEditor from "../../components/partAssemblyEditor/PartAssemblySingleEditor";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import usePartAssembly from "../../data/usePartAssembly";
import {
  FlowMoveDirection,
  IPartAssembly,
  IPartAssemblyOnly,
  ProductionFlowStage,
} from "../../models/Part";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import useNotification from "../../components/notifications/useNotifications";
import classNames from "../../utils/classNames";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";

const STAGE_LABELS: Record<ProductionFlowStage, string> = {
  [ProductionFlowStage.Triage]: "Triage",
  [ProductionFlowStage.Depollution]: "Depollution",
};

// A row in the flow is either a real assembly or one of the two stages. Stages are positioned with
// the same arrows but have nothing to edit - they are not assemblies, they only hold a place in the
// order, so there is no name, categories or cycle times behind them.
type FlowRow =
  | { kind: "assembly"; assembly: IPartAssembly }
  | { kind: "stage"; stage: ProductionFlowStage };

export default function PartAssemblyEditor() {
  const { assemblies, flow, create, moveFlowItem, setStagesEnabled } = usePartAssembly();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Assembly");
  const { addNotification } = useNotification();
  const hasProductionManager = CheckFeatureFlag("Production Manager");
  const [selectedAssembly, setSelectedAssembly] = useState<IPartAssembly | undefined>(undefined);
  const { register, handleSubmit, reset } = useForm<IPartAssemblyOnly>();
  const [open, setOpen] = useState(false);

  const stagesEnabled = flow.data?.data?.stagesEnabled ?? false;

  // The flow is the source of truth for the sequence, because it is the only thing that knows where
  // the two stages sit relative to the assemblies. Before it loads, fall back to the assembly list.
  const rows = useMemo<FlowRow[]>(() => {
    const assemblyList = assemblies.data?.data ?? [];
    const items = flow.data?.data?.items;

    if (!items?.length)
      return assemblyList.map((assembly) => ({ kind: "assembly", assembly } as FlowRow));

    const byId = new Map(assemblyList.map((assembly) => [assembly.id, assembly]));
    const built: FlowRow[] = [];

    items.forEach((item) => {
      if (item.stage) {
        built.push({ kind: "stage", stage: item.stage });
        return;
      }

      const assembly = item.assemblyId ? byId.get(item.assemblyId) : undefined;
      if (assembly) built.push({ kind: "assembly", assembly });
    });

    return built;
  }, [assemblies.data, flow.data]);

  const handleSelect = (assembly: IPartAssembly) => {
    setSelectedAssembly(assembly as IPartAssembly);
    setOpen(true);
  };

  const onSubmit = (data: IPartAssemblyOnly) => {
    if (!data.name) {
      //don't make anything
      addNotification({
        variant: "warning",
        primaryText: "Please enter a name for the assembly",
      });
      return;
    }

    create.mutate(data as IPartAssembly, saveHandlers);
    reset();
  };

  // One endpoint owns the whole move. Assembly order lives in the database and the stage positions
  // in customer settings, so working out which to write is the server's job, not the page's.
  const handleChangeOrder = (row: FlowRow, direction: FlowMoveDirection) => {
    moveFlowItem.mutate(
      row.kind === "stage"
        ? { stage: row.stage, direction }
        : { assemblyId: row.assembly.id, direction },
      saveHandlers
    );
  };

  const handleToggleStages = () => {
    setStagesEnabled.mutate(!stagesEnabled, saveHandlers);
  };

  const isLoading = assemblies.isLoading || flow.isLoading;

  return (
    <>
      <PartAssemblySingleEditor assembly={selectedAssembly} open={open} setOpen={setOpen} />
      <div className="flex flex-col gap-y-4">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-row flex-grow gap-x-4">
          <TextInput label={"Assembly Name"} register={register} registerName={"name"} />
          <ButtonInput isSubmit={true} label={"Create"} onClick={undefined} classes={"h-fit self-end"} />
        </form>

        {hasProductionManager && (
          <div className="rounded-md border border-gray-200 bg-white p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={stagesEnabled}
                disabled={setStagesEnabled.isLoading || flow.isLoading}
                onChange={handleToggleStages}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>Include Triage/Depollution</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              {stagesEnabled
                ? "Triage and Depollution appear as stages in the Production Dashboard flow. Position them with the arrows below; there is nothing else to edit on them."
                : "Triage and Depollution stay in their own panels at the bottom of the Production Dashboard, outside the assembly flow."}
            </p>
          </div>
        )}

        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            {isLoading ? <LoadingWheel /> : (
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
                  <thead className="bg-primary-50 text-gray-800">
                    <tr>
                      <th scope="col" className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left">
                        Assembly Name
                      </th>
                      <th scope="col" className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rows.map((row, index) => {
                      const isStage = row.kind === "stage";

                      return (
                        <tr
                          key={isStage ? "stage-" + row.stage : row.assembly.id}
                          onClick={isStage ? undefined : () => handleSelect(row.assembly)}
                          className={classNames(
                            isStage ? "bg-gray-50" : "hover:bg-gray-100 hover:cursor-pointer",
                            !isStage && selectedAssembly?.id === row.assembly.id ? "bg-green-100" : ""
                          )}
                        >
                          <td className="px-3 py-3 border text-sm">
                            {isStage ? (
                              <span className="flex items-center gap-x-2">
                                <span className="text-gray-700">{STAGE_LABELS[row.stage]}</span>
                                <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                                  Stage
                                </span>
                              </span>
                            ) : (
                              row.assembly.name
                            )}
                          </td>
                          <td>
                            <span className="flex flex-row gap-x-2 justify-end items-center text-sm">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleChangeOrder(row, FlowMoveDirection.Up); }}
                                disabled={index === 0 || moveFlowItem.isLoading}
                                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
                              >
                                <ArrowUpIcon className="h-4 w-4 inline-block mr-1" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleChangeOrder(row, FlowMoveDirection.Down); }}
                                disabled={index === rows.length - 1 || moveFlowItem.isLoading}
                                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
                              >
                                <ArrowDownIcon className="h-4 w-4 inline-block mr-1" />
                              </button>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
